import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckIcon, ImagePlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

interface IconGridProps {
	icons: string[];
	selectedUrl?: string | null;
	disabled?: boolean;
	showUpload?: boolean;
	showRemove?: boolean;
	initialVisibleCount?: number;
	onSelect?: (url: string) => void;
	onUploadClick?: () => void;
	onRemove?: (url: string) => void;
	className?: string;
}

export default function IconGrid({
	icons,
	selectedUrl,
	disabled = false,
	showUpload = false,
	showRemove = false,
	initialVisibleCount,
	onSelect,
	onUploadClick,
	onRemove,
	className,
}: IconGridProps) {
	const selectedIndex = selectedUrl ? icons.indexOf(selectedUrl) : -1;
	const autoExpand = initialVisibleCount !== undefined && selectedIndex >= initialVisibleCount;
	const selectionKey = `${selectedUrl ?? ''}:${icons.length}:${initialVisibleCount ?? ''}`;

	const [overrideExpanded, setOverrideExpanded] = useState<boolean | null>(null);
	const [prevSelectionKey, setPrevSelectionKey] = useState(selectionKey);

	if (selectionKey !== prevSelectionKey) {
		setPrevSelectionKey(selectionKey);
		setOverrideExpanded(null);
	}

	const expanded = overrideExpanded ?? autoExpand;

	const hasMore = initialVisibleCount !== undefined && icons.length > initialVisibleCount;
	const hiddenCount = hasMore ? icons.length - initialVisibleCount : 0;
	const visibleIcons = hasMore && !expanded ? icons.slice(0, initialVisibleCount) : icons;

	return (
		<div className='flex flex-col gap-2'>
		<div className={cn('grid grid-cols-4 sm:grid-cols-5 gap-2', className)}>
			{showUpload ? (
				<button
					type='button'
					disabled={disabled}
					onClick={onUploadClick}
					className='aspect-square rounded-xl border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center gap-1 cursor-pointer transition-all duration-150 hover:bg-muted/50 hover:border-muted-foreground/30 disabled:cursor-not-allowed disabled:opacity-60'
				>
					<ImagePlusIcon size={20} className='text-muted-foreground' />
					<span className='text-[10px] text-muted-foreground leading-tight'>Загрузить</span>
				</button>
			) : null}

			{visibleIcons.map(url => {
				const isSelected = selectedUrl === url;

				return (
					<div key={url} className='relative group'>
						<button
							type='button'
							disabled={disabled}
							onClick={() => onSelect?.(url)}
							className={cn(
								'relative aspect-square w-full rounded-xl border overflow-hidden bg-muted/30 cursor-pointer transition-all duration-150 hover:scale-[1.02] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60',
								isSelected ? 'ring-2 ring-primary ring-offset-2 border-primary/50' : 'border-border hover:border-muted-foreground/30',
							)}
						>
							<img src={url} alt='' className='size-full object-cover' />
							{isSelected ? (
								<span className='absolute inset-0 flex items-center justify-center bg-primary/20'>
									<span className='rounded-full bg-primary p-0.5 text-primary-foreground shadow-sm'>
										<CheckIcon size={14} />
									</span>
								</span>
							) : null}
						</button>

						{showRemove ? (
							<button
								type='button'
								disabled={disabled}
								onClick={e => {
									e.stopPropagation();
									onRemove?.(url);
								}}
								className='absolute -top-1.5 -right-1.5 z-10 rounded-full bg-background border border-border p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground hover:border-destructive disabled:cursor-not-allowed'
								aria-label='Удалить иконку'
							>
								<XIcon size={12} />
							</button>
						) : null}
					</div>
				);
			})}
		</div>

		{hasMore ? (
			<Button
				type='button'
				variant='ghost'
				size='sm'
				className='self-start h-8 px-2 text-muted-foreground'
				onClick={() => setOverrideExpanded(v => !(v ?? autoExpand))}
			>
				{expanded ? 'Скрыть' : `Ещё (+${hiddenCount})`}
			</Button>
		) : null}
		</div>
	);
}

interface IconGridSectionProps {
	title: string;
	count?: number;
	emptyText?: string;
	children: React.ReactNode;
}

export function IconGridSection({ title, count, emptyText, children }: IconGridSectionProps) {
	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center gap-2'>
				<p className='text-sm font-medium'>{title}</p>
				{typeof count === 'number' ? (
					<span className='text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full'>{count}</span>
				) : null}
			</div>
			{emptyText ? <p className='text-xs text-muted-foreground'>{emptyText}</p> : null}
			{children}
		</div>
	);
}
